import type { CoasterProject } from '../schema/index.ts';
import type { TrackSample, TrackSampler, Vec3 } from '../track/index.ts';
import { add, cross, dot, length, normalize, scale, sub } from '../track/index.ts';
import {
  coasterFamilyPresentation,
  type CoasterFamilyClearanceEnvelope,
} from '../ride/family-registry.ts';

export interface ClearanceConflict {
  readonly id: string;
  readonly severity: 'warning' | 'blocker';
  readonly sectionAId: string;
  readonly sectionBId: string;
  readonly pieceAId: string | null;
  readonly pieceBId: string | null;
  readonly distanceAM: number;
  readonly distanceBM: number;
  readonly measuredClearanceM: number;
  readonly requiredClearanceM: number;
  readonly closestPointA: Vec3;
  readonly closestPointB: Vec3;
  readonly policyVersion: string;
}

export interface ClearanceValidationResult {
  readonly policyVersion: string;
  readonly conflicts: readonly ClearanceConflict[];
  readonly minimumClearanceM: number;
}

interface OrientedPrism {
  readonly index: number;
  readonly startDistance: number;
  readonly endDistance: number;
  readonly center: Vec3;
  readonly axes: readonly [Vec3, Vec3, Vec3];
  readonly half: readonly [number, number, number];
  readonly sectionId: string;
  readonly pieceId: string | null;
  readonly exemptionPieceId: string | null;
  readonly midpoint: Vec3;
  readonly aabbMin: Vec3;
  readonly aabbMax: Vec3;
}

const INVERSION_PREFIXES = ['loop', 'roll_', 'corkscrew_'] as const;
const CELL_SIZE_M = 4;
const EPSILON = 1e-7;

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}

function frameAngleDegrees(left: TrackSample, right: TrackSample): number {
  const tangent = Math.acos(clamp(dot(left.tangent, right.tangent), -1, 1));
  const up = Math.acos(clamp(dot(left.up, right.up), -1, 1));
  return (Math.max(tangent, up) * 180) / Math.PI;
}

function adaptiveDistances(
  sampler: TrackSampler,
  maximumSpacingM: number,
  maximumFrameAngleDegrees: number,
): number[] {
  const distances: number[] = [0];
  const appendInterval = (start: number, end: number, depth: number): void => {
    const startSample = sampler.sampleByDistance(start);
    const endSample = sampler.sampleByDistance(end);
    if (
      depth < 8 &&
      end - start > maximumSpacingM / 8 &&
      frameAngleDegrees(startSample, endSample) > maximumFrameAngleDegrees
    ) {
      const midpoint = (start + end) * 0.5;
      appendInterval(start, midpoint, depth + 1);
      appendInterval(midpoint, end, depth + 1);
      return;
    }
    distances.push(end);
  };
  const intervalCount = Math.max(1, Math.ceil(sampler.length / maximumSpacingM));
  for (let index = 0; index < intervalCount; index += 1)
    appendInterval(
      (index / intervalCount) * sampler.length,
      ((index + 1) / intervalCount) * sampler.length,
      0,
    );
  return distances;
}

function averagedAxis(left: Vec3, right: Vec3, fallback: Vec3): Vec3 {
  const average = normalize(add(left, right));
  return length(average) > 0.5 ? average : fallback;
}

function prismAabb(
  center: Vec3,
  axes: readonly [Vec3, Vec3, Vec3],
  half: readonly [number, number, number],
): { min: Vec3; max: Vec3 } {
  const extent = {
    x:
      Math.abs(axes[0].x) * half[0] + Math.abs(axes[1].x) * half[1] + Math.abs(axes[2].x) * half[2],
    y:
      Math.abs(axes[0].y) * half[0] + Math.abs(axes[1].y) * half[1] + Math.abs(axes[2].y) * half[2],
    z:
      Math.abs(axes[0].z) * half[0] + Math.abs(axes[1].z) * half[1] + Math.abs(axes[2].z) * half[2],
  };
  return { min: sub(center, extent), max: add(center, extent) };
}

function buildPrisms(
  project: CoasterProject,
  sampler: TrackSampler,
  policy: CoasterFamilyClearanceEnvelope,
): OrientedPrism[] {
  const pieceBySection = new Map<string, string>();
  const exemptionBySection = new Map<string, string>();
  for (const piece of project.editor.pieceGroups) {
    const exempt = INVERSION_PREFIXES.some((prefix) => piece.definitionId.startsWith(prefix));
    for (const sectionId of piece.sectionIds) {
      pieceBySection.set(sectionId, piece.id);
      if (exempt) exemptionBySection.set(sectionId, piece.id);
    }
  }
  const distances = adaptiveDistances(
    sampler,
    policy.maximumSampleSpacingM,
    policy.maximumFrameAngleDegrees,
  );
  const verticalCenterM = (policy.aboveTrackM - policy.belowTrackM) * 0.5;
  const verticalHalfM = (policy.aboveTrackM + policy.belowTrackM) * 0.5;
  const prisms: OrientedPrism[] = [];
  for (let index = 0; index < distances.length - 1; index += 1) {
    const startDistance = distances[index]!;
    const endDistance = distances[index + 1]!;
    const start = sampler.sampleByDistance(startDistance);
    const end = sampler.sampleByDistance(endDistance);
    const sectionId = sampler.sampleByDistance((startDistance + endDistance) * 0.5).section?.id;
    if (!sectionId) continue;
    const tangent = normalize(sub(end.position, start.position));
    const up = averagedAxis(start.up, end.up, start.up);
    let right = normalize(cross(up, tangent));
    if (length(right) < 0.5) right = averagedAxis(start.right, end.right, start.right);
    const correctedUp = normalize(cross(tangent, right));
    const midpoint = scale(add(start.position, end.position), 0.5);
    const center = add(midpoint, scale(correctedUp, verticalCenterM));
    const axes = [tangent, right, correctedUp] as const;
    const half = [
      Math.max(0.02, length(sub(end.position, start.position)) * 0.5),
      policy.lateralHalfWidthM,
      verticalHalfM,
    ] as const;
    const aabb = prismAabb(center, axes, half);
    const advisoryExpansion = {
      x: policy.advisoryMarginM,
      y: policy.advisoryMarginM,
      z: policy.advisoryMarginM,
    };
    prisms.push({
      index: prisms.length,
      startDistance,
      endDistance,
      center,
      axes,
      half,
      sectionId,
      pieceId: pieceBySection.get(sectionId) ?? null,
      exemptionPieceId: exemptionBySection.get(sectionId) ?? null,
      midpoint,
      // The broad phase must include separated envelopes inside the advisory
      // margin, not only envelopes that already overlap.
      aabbMin: sub(aabb.min, advisoryExpansion),
      aabbMax: add(aabb.max, advisoryExpansion),
    });
  }
  return prisms;
}

function cellRange(minimum: number, maximum: number): readonly [number, number] {
  return [Math.floor(minimum / CELL_SIZE_M), Math.floor(maximum / CELL_SIZE_M)];
}

function candidatePairs(prisms: readonly OrientedPrism[]): readonly [number, number][] {
  const cells = new Map<string, number[]>();
  for (const prism of prisms) {
    const [minX, maxX] = cellRange(prism.aabbMin.x, prism.aabbMax.x);
    const [minY, maxY] = cellRange(prism.aabbMin.y, prism.aabbMax.y);
    const [minZ, maxZ] = cellRange(prism.aabbMin.z, prism.aabbMax.z);
    for (let x = minX; x <= maxX; x += 1)
      for (let y = minY; y <= maxY; y += 1)
        for (let z = minZ; z <= maxZ; z += 1) {
          const key = `${x}:${y}:${z}`;
          const entries = cells.get(key) ?? [];
          entries.push(prism.index);
          cells.set(key, entries);
        }
  }
  const unique = new Set<string>();
  for (const entries of cells.values())
    for (let left = 0; left < entries.length; left += 1)
      for (let right = left + 1; right < entries.length; right += 1) {
        const a = Math.min(entries[left]!, entries[right]!);
        const b = Math.max(entries[left]!, entries[right]!);
        unique.add(`${a}:${b}`);
      }
  return [...unique]
    .map((key) => key.split(':').map(Number) as [number, number])
    .sort((left, right) => left[0] - right[0] || left[1] - right[1]);
}

function routeAdjacent(left: OrientedPrism, right: OrientedPrism, sampler: TrackSampler): boolean {
  const directGap = Math.max(
    0,
    Math.max(left.startDistance, right.startDistance) -
      Math.min(left.endDistance, right.endDistance),
  );
  if (directGap <= EPSILON) return true;
  if (!sampler.closed) return false;
  const wrappedGap = Math.max(
    0,
    Math.min(left.startDistance, right.startDistance) +
      sampler.length -
      Math.max(left.endDistance, right.endDistance),
  );
  return wrappedGap <= EPSILON;
}

function connectedSections(
  left: OrientedPrism,
  right: OrientedPrism,
  sectionIndices: ReadonlyMap<string, number>,
  sectionCount: number,
  closed: boolean,
): boolean {
  if (left.sectionId === right.sectionId) return true;
  const leftIndex = sectionIndices.get(left.sectionId);
  const rightIndex = sectionIndices.get(right.sectionId);
  if (leftIndex === undefined || rightIndex === undefined) return false;
  const difference = Math.abs(leftIndex - rightIndex);
  return difference === 1 || (closed && difference === sectionCount - 1);
}

function projectionRadius(prism: OrientedPrism, axis: Vec3, margin = 0): number {
  return (
    Math.abs(dot(prism.axes[0], axis)) * prism.half[0] +
    Math.abs(dot(prism.axes[1], axis)) * (prism.half[1] + margin) +
    Math.abs(dot(prism.axes[2], axis)) * (prism.half[2] + margin)
  );
}

function separationOnAxes(left: OrientedPrism, right: OrientedPrism): number {
  const axes: Vec3[] = [...left.axes, ...right.axes];
  for (const leftAxis of left.axes)
    for (const rightAxis of right.axes) {
      const axis = normalize(cross(leftAxis, rightAxis));
      if (length(axis) > 0.5) axes.push(axis);
    }
  const delta = sub(right.center, left.center);
  let maximumGap = Number.NEGATIVE_INFINITY;
  for (const axis of axes) {
    const gap =
      Math.abs(dot(delta, axis)) - projectionRadius(left, axis) - projectionRadius(right, axis);
    maximumGap = Math.max(maximumGap, gap);
  }
  // Positive values are clear space between the two envelopes. Negative
  // values are penetration depth along the least-overlapping separating axis.
  return maximumGap;
}

function conflictKey(left: OrientedPrism, right: OrientedPrism): string {
  const first = `${left.pieceId ?? left.sectionId}:${left.sectionId}`;
  const second = `${right.pieceId ?? right.sectionId}:${right.sectionId}`;
  return first.localeCompare(second) <= 0 ? `${first}|${second}` : `${second}|${first}`;
}

export function validateTrackClearance(
  project: CoasterProject,
  sampler: TrackSampler,
): ClearanceValidationResult {
  const policy = coasterFamilyPresentation(project.coasterFamilyId).clearance;
  const prisms = buildPrisms(project, sampler, policy);
  const sectionIndices = new Map(
    project.track.sections.map((section, index) => [section.id, index] as const),
  );
  const conflicts = new Map<string, ClearanceConflict>();
  for (const [leftIndex, rightIndex] of candidatePairs(prisms)) {
    const left = prisms[leftIndex]!;
    const right = prisms[rightIndex]!;
    if (routeAdjacent(left, right, sampler)) continue;
    if (
      connectedSections(
        left,
        right,
        sectionIndices,
        project.track.sections.length,
        project.track.closed,
      )
    )
      continue;
    if (
      left.exemptionPieceId &&
      right.exemptionPieceId &&
      left.exemptionPieceId === right.exemptionPieceId
    )
      continue;
    const measuredClearanceM = separationOnAxes(left, right);
    if (measuredClearanceM > policy.advisoryMarginM) continue;
    const key = conflictKey(left, right);
    const severity = measuredClearanceM <= EPSILON ? 'blocker' : 'warning';
    const candidate: ClearanceConflict = {
      id: `${policy.version}:${key}`,
      severity,
      sectionAId: left.sectionId,
      sectionBId: right.sectionId,
      pieceAId: left.pieceId,
      pieceBId: right.pieceId,
      distanceAM: (left.startDistance + left.endDistance) * 0.5,
      distanceBM: (right.startDistance + right.endDistance) * 0.5,
      measuredClearanceM,
      requiredClearanceM: severity === 'blocker' ? 0 : policy.advisoryMarginM,
      closestPointA: left.midpoint,
      closestPointB: right.midpoint,
      policyVersion: policy.version,
    };
    const existing = conflicts.get(key);
    if (
      !existing ||
      (candidate.severity === 'blocker' && existing.severity !== 'blocker') ||
      candidate.measuredClearanceM < existing.measuredClearanceM
    )
      conflicts.set(key, candidate);
  }
  const ordered = [...conflicts.values()].sort(
    (left, right) =>
      Number(right.severity === 'blocker') - Number(left.severity === 'blocker') ||
      left.id.localeCompare(right.id),
  );
  return {
    policyVersion: policy.version,
    conflicts: ordered,
    minimumClearanceM: ordered.length
      ? Math.min(...ordered.map((conflict) => conflict.measuredClearanceM))
      : Number.POSITIVE_INFINITY,
  };
}

export function clearanceAtDistance(
  conflicts: readonly ClearanceConflict[],
  distanceM: number,
): number {
  let minimum = Number.POSITIVE_INFINITY;
  for (const conflict of conflicts)
    if (
      Math.abs(conflict.distanceAM - distanceM) <= 0.75 ||
      Math.abs(conflict.distanceBM - distanceM) <= 0.75
    )
      minimum = Math.min(minimum, conflict.measuredClearanceM);
  return minimum;
}
